import { Alert, Button, buttonClass, cardClass, selectClass, Table } from '@ledgerloom/ui';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, newKey, type Outcome } from '../api';
import { usePageTitle } from '../hooks';
import { useSession, isAdmin } from '../session';
import { Empty, ErrorBanner, Icon, Reasons, StateChip } from '../components/ui';
import { Field } from '../components/Field';
import { Stepper } from '../components/Stepper';
import { IMPORT_SYSTEMS } from '../format';
import { ImportSummary } from './Approvals';

interface UploadInfo { id: string; files: Array<{ name: string; rows: number; columns: string[]; sample: Array<Record<string, string>> }> }

const MAX_BYTES = 50 * 1024 * 1024;
const STEPS = ['Upload files', 'Check and choose', 'Review the dry run'];

// Import contacts in steps: pick files, map, dry run, propose. Patterns:
// advanced/wizard, forms/file-input, user-feedback/progress-indicator, and
// forms/form-validation.
export function Import() {
  const { me, refreshStats } = useSession();
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [upload, setUpload] = useState<UploadInfo | null>(null);
  const [policy, setPolicy] = useState<'merge' | 'review'>('merge');
  const [system, setSystem] = useState('csv');
  const [result, setResult] = useState<(Outcome & { mapping_warnings: string[] }) | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<'upload' | 'dry' | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const stepHead = useRef<HTMLHeadingElement>(null);
  usePageTitle('Import');

  const step = result ? 2 : upload ? 1 : 0;
  // Each new step takes focus so a keyboard or screen-reader user lands on it.
  useEffect(() => { if (step > 0) stepHead.current?.focus(); }, [step]);

  function pick(list: FileList | null) {
    const chosen = Array.from(list ?? []);
    const bad = chosen.filter((f) => !/\.csv$/i.test(f.name));
    const big = chosen.filter((f) => f.size > MAX_BYTES);
    setFileError(bad.length ? `${bad.map((f) => f.name).join(', ')} ${bad.length === 1 ? 'is not a CSV file' : 'are not CSV files'}.` : big.length ? `${big[0]!.name} is over 50 MB.` : null);
    setFiles(chosen.filter((f) => !bad.includes(f) && !big.includes(f)));
  }

  async function doUpload(e: FormEvent) {
    e.preventDefault();
    if (!files.length) { setFileError('Choose at least one CSV file.'); picker.current?.focus(); return; }
    setBusy('upload'); setError(null); setResult(null);
    try {
      const form = new FormData();
      for (const f of files) form.append('file', f, f.name);
      const saved = await api<{ id: string }>('/v1/uploads', { form });
      setUpload(await api<UploadInfo>(`/v1/uploads/${saved.id}`));
    } catch (err) { setError(err); } finally { setBusy(null); }
  }

  async function propose() {
    if (!upload) return;
    setBusy('dry'); setError(null);
    try {
      setResult(await api('/v1/imports', { body: { upload_id: upload.id, duplicate_policy: policy, system, idempotency_key: newKey() } }));
      refreshStats();
    } catch (err) { setError(err); } finally { setBusy(null); }
  }

  function restart() { setUpload(null); setResult(null); setFiles([]); setError(null); if (picker.current) picker.current.value = ''; }

  if (!isAdmin(me)) return (
    <div className="stack">
      <div className="page-head"><div><h1>Import</h1></div></div>
      <div className={cardClass}><Empty title="Imports need an owner or admin" action={<Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to="/admin/people">See who is on your team</Link>}>Ask an admin to import your contacts.</Empty></div>
    </div>
  );

  return (
    <div className="stack" style={{ maxWidth: 920 }}>
      <div className="page-head"><div><h1>Import</h1><p>Move your contacts in without losing anything. You see a dry run before a single record is written.</p></div></div>
      <Stepper steps={STEPS} current={step} />
      <ErrorBanner error={error} focusOnShow />

      {step === 0 && (
        <form className={`${cardClass} card-pad stack`} onSubmit={doUpload} noValidate>
          <h2>Upload CSV files</h2>
          <Field label="CSV files" error={fileError} hint={<>Use the Mazu format: <span className="mono">parties.csv</span>, optional <span className="mono">relationships.csv</span>, and <span className="mono">users.csv</span> for your team. CSV only, up to 50 MB each. Files from another CRM go through the Migration agent, which maps their columns.</>}>
            <input ref={picker} type="file" accept=".csv,text/csv" multiple onChange={(e) => pick(e.target.files)} />
          </Field>
          {files.length > 0 && (
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }} aria-label="Chosen files">
              {files.map((f) => <li key={f.name}><span className="mono">{f.name}</span> <span className="faint">{(f.size / 1024).toFixed(0)} KB</span></li>)}
            </ul>
          )}
          <div className="sr-only" role="status" aria-live="polite">{files.length ? `${files.length} file${files.length === 1 ? '' : 's'} chosen` : ''}</div>
          <div><Button type="submit" disabled={busy === 'upload'} aria-busy={busy === 'upload'}><Icon name="upload" />{busy === 'upload' ? 'Uploading…' : 'Upload and continue'}</Button></div>
        </form>
      )}

      {step === 1 && upload && (
        <div className={`${cardClass} card-pad stack`}>
          <h2 ref={stepHead} tabIndex={-1}>Check the files</h2>
          {upload.files.map((f) => (
            <div key={f.name} className="stack" style={{ gap: 6 }}>
              <div className="row"><strong className="mono">{f.name}</strong><span className="faint">{f.rows.toLocaleString()} rows · {f.columns.length} columns</span></div>
              <Table label={`First rows of ${f.name}`} tableClassName="small">
                <caption className="sr-only">First rows of {f.name}</caption>
                <thead><tr>{f.columns.slice(0, 8).map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
                <tbody>{f.sample.slice(0, 3).map((r, i) => <tr key={i}>{f.columns.slice(0, 8).map((c) => <td key={c}>{r[c]}</td>)}</tr>)}</tbody>
              </Table>
            </div>
          ))}
          <div className="form-grid">
            <Field label="Coming from">
              <select className={selectClass} value={system} onChange={(e) => setSystem(e.target.value)}>
                {IMPORT_SYSTEMS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </Field>
            <Field label="Duplicates" hint="Nothing is deleted either way. Merged records are kept with the status merged.">
              <select className={selectClass} value={policy} onChange={(e) => setPolicy(e.target.value as 'merge' | 'review')}>
                <option value="merge">Merge clear duplicates, send the unsure ones to the agent</option>
                <option value="review">Keep every row, send all duplicates to the agent</option>
              </select>
            </Field>
          </div>
          <div className="row">
            <Button type="button" variant="ghost" onClick={restart}>Back: choose other files</Button>
            <Button type="button" disabled={busy === 'dry'} aria-busy={busy === 'dry'} onClick={propose}>{busy === 'dry' ? 'Running the dry run…' : 'Run the dry run'}</Button>
          </div>
        </div>
      )}

      {step === 2 && result && (
        <div className={`${cardClass} card-pad stack`}>
          <div className="row"><h2 className="grow" ref={stepHead} tabIndex={-1}>Dry run</h2><StateChip state={result.state} /></div>
          {result.mapping_warnings.length > 0 && <Alert role="note" tone="warning">{result.mapping_warnings.join(' · ')}</Alert>}
          {result.state === 'rejected'
            ? <Alert tone="danger" role="alert"><strong>The gate rejected this import.</strong><Reasons reasons={result.reasons} /></Alert>
            : <ImportSummary dry={result.computed?.import_dry_run} policy={policy} />}
          {result.state === 'pending_approval' && (
            <Alert tone="info" role="status">
              Nothing is written yet. {result.route?.signoffs_required === 1 && result.route.assignee_role === 'admin' ? 'Sign it off' : 'It needs a sign-off'} under <Link to="/desk/needs-you">Needs you</Link>.
            </Alert>
          )}
          <div className="row">
            {result.state === 'pending_approval' && <Link className={buttonClass()} to="/desk/needs-you">Open Needs you</Link>}
            <Button type="button" variant="ghost" onClick={restart}>Start another import</Button>
          </div>
        </div>
      )}
    </div>
  );
}
